import { buildRedirectUrl } from './buildRedirectUrl'

export interface NotificationLinkSource {
  type: string
  context?: unknown
  scopeType?: string | null
  scopeIri?: string | null
}

/**
 * Parses a notification `context` (JSON string or already-decoded object) into a flat map.
 */
export function parseNotificationContext(ctx: unknown): Record<string, string> {
  if (ctx == null) return {}
  if (typeof ctx === 'string') {
    try {
      const parsed = JSON.parse(ctx)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {}
    } catch {
      return {}
    }
  }
  if (typeof ctx === 'object' && !Array.isArray(ctx)) {
    return ctx as Record<string, string>
  }
  return {}
}

/**
 * Builds the `/redirect` URL a notification links to, carrying the audience scope so RedirectView
 * can switch context deterministically. Single source of truth shared by the notification list
 * item and the realtime toast (so both always link to the same destination). Returns null when
 * the notification has no actionable target.
 */
export function buildNotificationLink(
  n: NotificationLinkSource
): string | null {
  const ctx = parseNotificationContext(n.context)
  const withScope = (
    extra?: Record<string, string>
  ): Record<string, string> => ({
    ...extra,
    scopeType: n.scopeType ?? '',
    scopeIri: n.scopeIri ?? '',
  })

  if (
    n.type === 'network_circle_join_request' &&
    ctx.circleId &&
    ctx.requestId
  ) {
    return buildRedirectUrl(
      ctx.circleId,
      'networks',
      withScope({ invitationId: ctx.requestId })
    )
  }
  if (n.type === 'network_community_joined' && ctx.networkId) {
    return buildRedirectUrl(ctx.networkId, 'show', withScope())
  }
  if (ctx.circleId) {
    let action = 'view'
    if (
      n.type === 'circle_join_request' ||
      n.type === 'circle_join_request_cancelled'
    ) {
      action = 'members'
    } else if (
      n.type === 'circle_promoted_to_admin' ||
      n.type === 'circle_promoted_to_owner'
    ) {
      action = 'dashboard'
    } else if (
      n.type === 'circle_demoted_from_admin' ||
      n.type === 'circle_demoted_to_member'
    ) {
      action = 'members'
    }
    return buildRedirectUrl(ctx.circleId, action, withScope())
  }
  if (ctx.bookingId) {
    return buildRedirectUrl(ctx.bookingId, 'view', withScope())
  }
  if (ctx.materialId) {
    return buildRedirectUrl(ctx.materialId, 'view', withScope())
  }
  return null
}
